import { AuthUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import { Subscription } from "rxjs";
import type { Schema } from "../../amplify/data/resource";
import I18nTextUtils, { I18nTextKeys, LanguageDetail } from "./i18n/I18nTextUtils";
import ErrorHandlerUtils from "./ui/ErrorHandlerUtils";
import { LatLngBounds } from "leaflet";

const dbClient = generateClient<Schema>()

export interface PlaceDefinition {
    placeId: string,
    lat: number,
    lng: number,
    addressString?: string
    name: InternationalizedText[]
    description?: InternationalizedText[],
    placeCategories: string[],
    placeProperties?: string[],
    creatorUserId?: string,
    averageRating?: number,
    numberOfRatings?: number,
}

export interface NewPlaceDefinition {
    id: string,
    lat: number,
    lng: number,
    addressString?: string
    name: string
    description?: string,
    placeCategories: string[],
    placeProperties?: string[],
    creatorUserId: string,
}

export interface PlaceSummary {
    totalCount: number,
    isAtLimit: boolean, // true if limit was reached (100 locations returned)
    categories: Map<string, number>, // category -> count
    centerLat: number, // Bounds center (for marker positioning)
    centerLng: number, // Bounds center (for marker positioning)
    calculatedCenterLat: number, // Mean lat of returned places (for zoom center)
    calculatedCenterLng: number // Mean lng of returned places (for zoom center)
}

export interface LocationInfo {
    id: string,
    lat: number,
    lng: number,
    addressString?: string
}

export type CategoryDetail = {
    category: string
    iconPath: string
}

export type PropertyDetail = {
    property: string
}

export type InternationalizedText = {
    content: string
    locale: string
}

export enum RatingStatus {
    ACTIVE = 'ACTIVE',
    DELETED = 'DELETED'
}

export interface RatingDefinition {
    id: string,
    rating: number,
    ratingText: InternationalizedText[],
    placeId: string,
    creatorUserId: string,
    status: RatingStatus
}

export interface NewRatingDefinition {
    rating: number,
    ratingText: string,
    placeId: string,
    creatorUserId: string,
    status: RatingStatus
}

let createSubscription: Subscription
let deleteSubscription: Subscription

const categoryDetails: CategoryDetail[] = [
    { category: 'PLAYGROUND', iconPath: "/icons/places/Playground.svg" },
    { category: 'AMUSMENT_PARK', iconPath: "/icons/places/Castle-14.svg" },
    { category: 'AQUAPARK', iconPath: "/icons/places/Beach_resort-14.svg" },
    { category: 'POOL', iconPath: "/icons/places/Swimming-16.svg" },
    { category: 'REFRESHMENT', iconPath: "/icons/places/Outdoor_seating-14.svg" },
    { category: 'RESTAURANT', iconPath: "/icons/places/Restaurant-14.svg" },
    { category: 'PARK_GARDEN', iconPath: "/icons/places/Picnic_site.svg" },
    { category: 'MUSEUM_EDUCATIONAL', iconPath: "/icons/places/Museum-16.svg" },
    { category: 'ZOO_AQUARIUM', iconPath: "/icons/places/Dog_park.svg" },
    { category: 'BATHROOM', iconPath: "/icons/places/Toilets-16.svg" },
    { category: 'OTHER', iconPath: "/icons/places/Tree-16.svg" }
]

const propertyDetails: PropertyDetail[] = [
    { property: 'INDOOR' },
    { property: 'OUTDOOR' },
    { property: 'AGE_0_3' },
    { property: 'AGE_4_6' },
    { property: 'AGE_7_12' },
    { property: 'AGE_13_PLUS' },
    { property: 'AGE_ADULTS' },
    { property: 'PAID_FREE' },
    { property: 'PAID_PAID' },
    { property: 'PAID_EXPENSIVE' },
    { property: 'SIZE_TINY' },
    { property: 'SIZE_SMALL' },
    { property: 'SIZE_BIG' },
    { property: 'SIZE_GRANDE' },

    { property: 'BATHROOM_IN_OR_NEAR' },

    { property: 'ACCESSIBILITY_FOR_STROLLER' },
    { property: 'ACCESSIBILITY_FOR_WHEELCHAIR' },
    { property: 'ACCESSIBILITY_PARKING_NEARBY' },
    { property: 'ACCESSIBILITY_PUBLIC_TRANSPORT' },

    { property: 'FREE_ACCESS_AREA' },
    { property: 'RESTRICTED_ACCESS_AREA' },

    { property: 'AUDULTS_WIFI' },
    { property: 'CELULAR_SIGNAL_CALLS' },
    { property: 'CELULAR_SINGAL_DATA' }
]

export default class PlaceDataUtils {

    public static createNewPlace = async (newPlaceDefinition: NewPlaceDefinition, language: LanguageDetail) => {

        // Create place
        const { data: place, errors: placeErrors } = await dbClient.models.Place.create(
            {
                id: newPlaceDefinition.id,
                placeCategories: newPlaceDefinition.placeCategories,
                placeProperties: newPlaceDefinition.placeProperties,
                creatorUserId: newPlaceDefinition.creatorUserId
            },
            // Options
            {
                authMode: 'userPool'
            }
        )

        // Create location
        const { data: location, errors: locationErrors } = await dbClient.models.Location.create(
            {
                lat: newPlaceDefinition.lat,
                lng: newPlaceDefinition.lng,
                addressString: newPlaceDefinition.addressString,
                placeId: place?.id,
                creatorUserId: newPlaceDefinition.creatorUserId
            },
            // Options
            {
                authMode: 'userPool'
            }
        )

        if (locationErrors) {
            ErrorHandlerUtils.notifyDbError(locationErrors)
        }

        // TODO handle errors better!
        if (place) {
            // Store i18n texts
            const { data: placeNameText, errors: placeNameErrors } = await dbClient.models.InternationalizedTexts.create(
                // Model
                {
                    content: newPlaceDefinition.name,
                    locale: language.languageCode,
                    referenceId: place?.id,
                    referenceType: I18nTextKeys.Places.PLACE_NAME,
                    creatorUserId: newPlaceDefinition.creatorUserId
                },
                // Options
                {
                    authMode: 'userPool'
                })

            if (placeNameErrors) {
                ErrorHandlerUtils.notifyDbError(placeNameErrors)
                // return;
            }

            if (newPlaceDefinition.description) {
                const { data: placeDescriptionText, errors: placeDescriptionErrors } = await dbClient.models.InternationalizedTexts.create(
                    {
                        content: newPlaceDefinition.description,
                        locale: language.languageCode,
                        referenceId: place?.id,
                        referenceType: I18nTextKeys.Places.PLACE_DESCRIPTION,
                        creatorUserId: newPlaceDefinition.creatorUserId
                    },
                    // Options
                    {
                        authMode: 'userPool'
                    }
                )
                if (placeDescriptionErrors) {
                    ErrorHandlerUtils.notifyDbError(placeDescriptionErrors)
                    // return;
                }

            }
        }

    }

    private static getInternationalizedText = async (user: AuthUser | undefined, languageDetail: LanguageDetail, referenceId: string, referenceType: string)
        : Promise<InternationalizedText[] | undefined> => {
        const { data: texts, errors: textErrors } = await dbClient.models.InternationalizedTexts.list({
            filter: {
                and: [
                    {
                        referenceId: { eq: referenceId }
                    }, {
                        referenceType: { eq: referenceType }
                    }
                ]
            },
            limit: 10000,
            authMode: user ? "userPool" : "identityPool"
        })

        if (textErrors) {
            ErrorHandlerUtils.notifyDbError(textErrors)
            return;
        }
        // else if (texts.length != 1) {
        //     console.warn("Texts in error: ", texts)
        //     console.warn("Texts request data referenceType: ", referenceType, ", referenceId: ", referenceId, ", langDetail: ", languageDetail)
        //     ErrorHandlerUtils.notifyErrorString("Wrong number of found texts found!\n" + texts.join('\n'))
        //     return;
        // }

        return texts as InternationalizedText[]
    }

    public static getPlacesList = async (user: AuthUser | undefined): Promise<PlaceDefinition[]> => {
        const placesList: PlaceDefinition[] = []
        let failed = false;

        const { data: locations, errors: locationErrors } = await dbClient.models.Location.list({
            // TODO reduce limit - refactor loading
            limit: 1000,
            authMode: user ? "userPool" : "identityPool"
        })

        if (locationErrors) {
            ErrorHandlerUtils.notifyDbError(locationErrors)
            failed = true;
        }

        // forEach does not await finalization of all locations processing - .map() with await Promise.all() works
        await Promise.all(locations.map(async location => {
            const placeDefinition: PlaceDefinition = {
                placeId: location.placeId || '',
                lat: location.lat,
                lng: location.lng,
                addressString: location.addressString ? location.addressString : "",
                name: [],
                placeCategories: [],
                creatorUserId: location.creatorUserId
            }

            // Only work with locations with owningPlace now
            if (!location.placeId)
                return;

            const { data: place } = await location.owningPlace();

            if (place) {
                // placeId is important for image viewing!
                placeDefinition.placeId = place.id
                placeDefinition.placeCategories = place.placeCategories.filter(c => c != null)
                placeDefinition.placeProperties = place.placeProperties?.filter(c => c != null)
                // Prefer Place.creatorUserId, fallback to Location.creatorUserId for backward compatibility
                placeDefinition.creatorUserId = place.creatorUserId || location.creatorUserId

                // Get average rating for the place
                placeDefinition.averageRating = await this.getPlaceAverageRating(place.id, user)
                // Get number of ratings for the place
                const ratings = await this.getPlaceRatings(place.id, user)
                placeDefinition.numberOfRatings = ratings.length

                // TODO is this duplicate? See resolveInternationalizedText() in PlaceOverviewPopup.tsx
                const name = await this.getInternationalizedText(
                    user,
                    I18nTextUtils.getCurrentLanguageDetail(),
                    place?.id,
                    I18nTextKeys.Places.PLACE_NAME
                )
                if (name)
                    placeDefinition.name = name

                const description = await this.getInternationalizedText(
                    user,
                    I18nTextUtils.getCurrentLanguageDetail(),
                    place?.id,
                    I18nTextKeys.Places.PLACE_DESCRIPTION
                )
                if (description)
                    placeDefinition.description = description
            }
            else {
                // If place is not found - do not add empty location
                return
            }

            placesList.push(placeDefinition)
        }))

        return new Promise<PlaceDefinition[]>((resolve) => {
            console.info("In Promise placesList: ", placesList)
            resolve(placesList)
        })
    }

    public static getPlacesListForBounds = async (
        user: AuthUser | undefined, 
        bounds: LatLngBounds, 
        tolerance: number = 0.1,
        alreadyKnownPlaces: PlaceDefinition[] = [], // alreadyKnownPlaces are used to avoid unnecessary DB details gathering
        setPlaces: (places: PlaceDefinition[]) => void
    ): Promise<void> => {
        // Add tolerance to bounds
        const extendedBounds = {
            north: bounds.getNorth() + tolerance,
            south: bounds.getSouth() - tolerance,
            east: bounds.getEast() + tolerance,
            west: bounds.getWest() - tolerance
        }

        const { data: locations, errors: locationErrors } = await dbClient.models.Location.list({
            filter: {
                and: [
                    { lat: { between: [extendedBounds.south, extendedBounds.north] } },
                    { lng: { between: [extendedBounds.west, extendedBounds.east] } }
                ]
            },
            limit: 1000,
            authMode: user ? "userPool" : "identityPool"
        })

        if (locationErrors) {
            ErrorHandlerUtils.notifyDbError(locationErrors)
            return
        }

        const placesToReturn: PlaceDefinition[] = []
        let newPlacesAdded = 0

        // Process locations in parallel
        await Promise.all(locations.map(async location => {
            if (!location.placeId) return

            // If place is already known, add it to placesToReturn array and skip details gathering from DB
            const existingPlace = alreadyKnownPlaces.find(p => p.placeId === location.placeId)
            if (existingPlace) {
                placesToReturn.push(existingPlace)
                return
            }

            // This is a new place that needs to be fetched from DB
            newPlacesAdded++

            const placeDefinition: PlaceDefinition = {
                placeId: location.placeId,
                lat: location.lat,
                lng: location.lng,
                addressString: location.addressString ? location.addressString : "",
                name: [],
                placeCategories: [],
                creatorUserId: location.creatorUserId
            }

            const { data: place } = await location.owningPlace()

            if (place) {
                placeDefinition.placeId = place.id
                placeDefinition.placeCategories = place.placeCategories.filter(c => c != null)
                placeDefinition.placeProperties = place.placeProperties?.filter(c => c != null)
                // Prefer Place.creatorUserId, fallback to Location.creatorUserId for backward compatibility
                placeDefinition.creatorUserId = place.creatorUserId || location.creatorUserId

                placeDefinition.averageRating = await this.getPlaceAverageRating(place.id, user)
                const ratings = await this.getPlaceRatings(place.id, user)
                placeDefinition.numberOfRatings = ratings.length

                const name = await this.getInternationalizedText(
                    user,
                    I18nTextUtils.getCurrentLanguageDetail(),
                    place?.id,
                    I18nTextKeys.Places.PLACE_NAME
                )
                if (name)
                    placeDefinition.name = name

                const description = await this.getInternationalizedText(
                    user,
                    I18nTextUtils.getCurrentLanguageDetail(),
                    place?.id,
                    I18nTextKeys.Places.PLACE_DESCRIPTION
                )
                if (description)
                    placeDefinition.description = description

                placesToReturn.push(placeDefinition)
            }
        }))

        // Log summary
        console.info(`Places update summary:
        - Previously known places: ${alreadyKnownPlaces.length}
        - New places added: ${newPlacesAdded}
        - Total places after update: ${placesToReturn.length}`)

        console.debug("New places array size: ", placesToReturn.length)
        setPlaces(placesToReturn)
    }

    /**
     * Gets summary of places for bounds (count and categories only)
     * Optimized for low zoom levels where individual place details are not needed
     */
    public static getPlacesSummaryForBounds = async (
        user: AuthUser | undefined,
        bounds: LatLngBounds,
        tolerance: number = 0.1
    ): Promise<PlaceSummary | null> => {
        // Add tolerance to bounds
        const extendedBounds = {
            north: bounds.getNorth() + tolerance,
            south: bounds.getSouth() - tolerance,
            east: bounds.getEast() + tolerance,
            west: bounds.getWest() - tolerance
        }

        const { data: locations, errors: locationErrors } = await dbClient.models.Location.list({
            filter: {
                and: [
                    { lat: { between: [extendedBounds.south, extendedBounds.north] } },
                    { lng: { between: [extendedBounds.west, extendedBounds.east] } }
                ]
            },
            limit: 100,
            authMode: user ? "userPool" : "identityPool"
        })

        if (locationErrors) {
            ErrorHandlerUtils.notifyDbError(locationErrors)
            return null
        }

        if (!locations || locations.length === 0) {
            return null
        }

        // Check if we hit the limit (100 locations returned means there might be more)
        const isAtLimit = locations.length === 100

        // Calculate center point from bounds (for marker positioning)
        const centerLat = bounds.getCenter().lat
        const centerLng = bounds.getCenter().lng

        // Get only place categories without fetching full place details
        const categoryMap = new Map<string, number>()
        let totalCount = 0
        let sumLat = 0
        let sumLng = 0
        let validLocationCount = 0

        // Process locations to get categories and calculate mean lat/lng
        // Note: We still fetch the full place object, but only use placeCategories
        // This is still more efficient than fetching internationalized texts, ratings, etc.
        await Promise.all(locations.map(async location => {
            if (!location.placeId) return

            totalCount++
            
            // Accumulate lat/lng for mean calculation
            sumLat += location.lat
            sumLng += location.lng
            validLocationCount++

            // Get place to access categories
            const { data: place } = await location.owningPlace()

            if (place && place.placeCategories && place.placeCategories.length > 0) {
                const primaryCategory = place.placeCategories[0] || 'OTHER'
                categoryMap.set(primaryCategory, (categoryMap.get(primaryCategory) || 0) + 1)
            } else {
                // If place not found or has no categories, count as OTHER
                categoryMap.set('OTHER', (categoryMap.get('OTHER') || 0) + 1)
            }
        }))

        // Calculate mean lat/lng of returned places
        const calculatedCenterLat = validLocationCount > 0 ? sumLat / validLocationCount : centerLat
        const calculatedCenterLng = validLocationCount > 0 ? sumLng / validLocationCount : centerLng

        return {
            totalCount,
            isAtLimit,
            categories: categoryMap,
            centerLat,
            centerLng,
            calculatedCenterLat,
            calculatedCenterLng
        }
    }

    public static getPlaceCategories = (): string[] => {
        return categoryDetails.map(category => category.category)
    }

    public static getPlaceCategoryDetail = (categoryName: string): CategoryDetail | undefined => {
        return categoryDetails.find(cd => cd.category == categoryName)
    }

    public static getPlaceProperties = (): string[] => {
        return propertyDetails.map(property => property.property)
    }

    // Currently not used
    public static subscribePlaceChanges = async (user: AuthUser | undefined, setPlacesDefinitions: (places: PlaceDefinition[]) => void) => {
        createSubscription = dbClient.models.Location.onCreate({
            authMode: user ? "userPool" : "identityPool"
        }).subscribe({
            next: async (data) => {
                try {
                    setPlacesDefinitions(await this.getPlacesList(user))
                } catch (error) {
                    console.error("Cought subscription error on create: ", error);
                }
            },

        })

        deleteSubscription = dbClient.models.Location.onDelete({
            authMode: user ? "userPool" : "identityPool"
        }).subscribe({
            next: async (data) => {
                try {
                    setPlacesDefinitions(await this.getPlacesList(user))
                } catch (error) {
                    console.error("Cought subscription error on delete: ", error);
                }
            }
        })
    }

    public static unsubsribeAll = () => {
        createSubscription && createSubscription.unsubscribe()
        deleteSubscription && deleteSubscription.unsubscribe()
    }

    public static async createNewRating(ratingDefinition: NewRatingDefinition, language: LanguageDetail): Promise<void> {
        const { data: rating, errors: ratingErrors } = await dbClient.models.Rating.create(
            {
                rating: ratingDefinition.rating,
                placeId: ratingDefinition.placeId,
                creatorUserId: ratingDefinition.creatorUserId,
                status: ratingDefinition.status
            },
            {
                authMode: 'userPool'
            }
        )

        if (ratingErrors) {
            ErrorHandlerUtils.notifyDbError(ratingErrors)
            return
        }

        if (rating && ratingDefinition.ratingText) {
            const { data: ratingText, errors: ratingTextErrors } = await dbClient.models.InternationalizedTexts.create(
                {
                    content: ratingDefinition.ratingText,
                    locale: language.languageCode,
                    referenceId: rating.id,
                    referenceType: I18nTextKeys.Places.PLACE_RATING_COMMENT,
                    creatorUserId: ratingDefinition.creatorUserId
                },
                {
                    authMode: 'userPool'
                }
            )

            if (ratingTextErrors) {
                ErrorHandlerUtils.notifyDbError(ratingTextErrors)
            }
        }
    }

    public static getPlaceRatings = async (placeId: string, user: AuthUser | undefined): Promise<RatingDefinition[]> => {
        const { data: ratings, errors: ratingErrors } = await dbClient.models.Rating.list({
            filter: {
                and: [
                    { placeId: { eq: placeId } },
                    { status: { eq: RatingStatus.ACTIVE } }
                ]
            },
            limit: 100,
            authMode: user ? "userPool" : "identityPool"
        })

        if (ratingErrors) {
            ErrorHandlerUtils.notifyDbError(ratingErrors)
            return []
        }

        const ratingDefinitions: RatingDefinition[] = []

        for (const rating of ratings) {
            const ratingTexts = await this.getInternationalizedText(
                user,
                I18nTextUtils.getCurrentLanguageDetail(),
                rating.id,
                I18nTextKeys.Places.PLACE_RATING_COMMENT
            )

            if (ratingTexts && ratingTexts.length > 0) {
                ratingDefinitions.push({
                    id: rating.id,
                    rating: rating.rating,
                    ratingText: ratingTexts,
                    placeId: rating.placeId,
                    creatorUserId: rating.creatorUserId,
                    status: RatingStatus.ACTIVE
                })
            }
        }

        return ratingDefinitions
    }

    // TODO: Optimize this to use aggregate query to calculate average directly in the database
    public static getPlaceAverageRating = async (placeId: string, user: AuthUser | undefined): Promise<number> => {
        // Using aggregate query to calculate average directly in the database
        const { data: ratings, errors: ratingErrors } = await dbClient.models.Rating.list({
            filter: {
                and: [
                    { placeId: { eq: placeId } },
                    { status: { eq: RatingStatus.ACTIVE } }
                ]
            },
            selectionSet: ['rating'], // Only fetch the rating field
            authMode: user ? "userPool" : "identityPool"
        })

        if (ratingErrors) {
            ErrorHandlerUtils.notifyDbError(ratingErrors)
            return 0
        }

        if (!ratings || ratings.length === 0) {
            return 0
        }

        // Calculate average
        const sum = ratings.reduce((acc, curr) => acc + curr.rating, 0)
        return sum / ratings.length
    }

    public static async getUserPlaceRating(placeId: string, user: AuthUser): Promise<RatingDefinition | undefined> {
        const { data: ratings, errors: ratingErrors } = await dbClient.models.Rating.list({
            filter: {
                and: [
                    { placeId: { eq: placeId } },
                    { creatorUserId: { eq: user.userId } },
                    { status: { eq: RatingStatus.ACTIVE } }
                ]
            },
            authMode: user ? "userPool" : "identityPool"
        })

        if (ratingErrors) {
            ErrorHandlerUtils.notifyDbError(ratingErrors)
            return undefined
        }

        if (!ratings || ratings.length === 0) {
            return undefined
        }

        // Only one rating per place per user is expected
        const rating = ratings[0]
        const ratingTexts = await this.getInternationalizedText(
            user,
            I18nTextUtils.getCurrentLanguageDetail(),
            rating.id,
            I18nTextKeys.Places.PLACE_RATING_COMMENT
        )

        if (ratingTexts && ratingTexts.length > 0) {
            return {
                id: rating.id,
                rating: rating.rating,
                ratingText: ratingTexts,
                placeId: rating.placeId,
                creatorUserId: rating.creatorUserId,
                status: RatingStatus.ACTIVE
            }
        }

        return undefined
    }

    public static async deleteUserRating(ratingId: string, user: AuthUser): Promise<void> {
        const { errors: ratingErrors } = await dbClient.models.Rating.update(
            {
                id: ratingId,
                status: RatingStatus.DELETED
            },
            {
                authMode: 'userPool'
            }
        )

        if (ratingErrors) {
            ErrorHandlerUtils.notifyDbError(ratingErrors)
        }
    }

}
